import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { certificateReportService } from "../services/certificateReportService";
import { CertificateReportSearchRequest } from "../models/certificateReport.model";

/**
 * Runs the By Certificates report search. `request` is null until the user clicks "Run Report" —
 * an empty/unset filter must never auto-query (FS_05_Reporting_01 — empty filter = no-op, not
 * "match everyone"). Independent of the technology report's hook — no shared "report query" hook.
 *
 * `runId` exists purely to force a fresh fetch on every explicit "Run Report" click. React Query
 * hashes `queryKey` by value, not reference — clicking "Run Report" twice with an unchanged filter
 * produces a value-identical `request` object, which would otherwise hash to the same key and
 * silently reuse the cached result instead of refetching (looked like "Run Report only works
 * once"). Bumping `runId` on every click guarantees a distinct key each time, independent of
 * whether the filter itself changed. Paging (same run, different page) intentionally does NOT
 * bump it, so per-page caching within one run still works normally.
 */
export const useCertificateReportSearch = (request: CertificateReportSearchRequest | null, runId = 0) => {
  return useQuery({
    queryKey: ["reports", "certificates", "search", request, runId],
    queryFn: () => certificateReportService.search(request as CertificateReportSearchRequest),
    enabled: request !== null,
    placeholderData: keepPreviousData,
  });
};
